'use client';

import { useState, type ReactNode } from 'react';
import { ChevronDown, List } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import DocsSidebar from './DocsSidebar';
import OnThisPage from './OnThisPage';
import PageNav from './PageNav';

export default function DocsShell({ children }: { children: ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen bg-black">
      <div className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-rose-900/10 via-black to-black" />
        <div className="absolute left-1/2 top-0 h-[500px] w-[500px] -translate-x-1/2 rounded-full bg-rose-500/5 blur-3xl" />
      </div>

      <Header />

      <div className="mx-auto flex w-full max-w-[1500px] gap-0 px-4 sm:px-6 lg:px-8">
        {/* Left sidebar - desktop */}
        <aside className="hidden lg:block lg:w-60 lg:flex-shrink-0">
          <div className="custom-scrollbar sticky top-16 max-h-[calc(100vh-4rem)] overflow-y-auto py-10 pr-6">
            <DocsSidebar />
          </div>
        </aside>

        {/* Main + on-this-page */}
        <div className="min-w-0 flex-1 py-10 lg:border-l lg:border-neutral-900 lg:pl-10">
          {/* Mobile nav trigger */}
          <div className="mb-8 lg:hidden">
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              aria-expanded={mobileOpen}
              className="flex w-full items-center justify-between border border-neutral-800/60 bg-neutral-950/50 px-4 py-3 text-sm text-white"
            >
              <span className="flex items-center gap-2">
                <List className="h-4 w-4 text-rose-400" />
                Documentation menu
              </span>
              <ChevronDown className={`h-4 w-4 text-neutral-500 transition-transform ${mobileOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileOpen && (
              <div className="mt-2 border border-neutral-800/60 bg-neutral-950/50 p-4">
                <DocsSidebar onNavigate={() => setMobileOpen(false)} />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-12 xl:grid-cols-[minmax(0,1fr)_13rem]">
            <article className="min-w-0">
              {children}
              <PageNav />
            </article>
            <OnThisPage />
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
